import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  ListIcon,
  PlusIcon,
  TagsIcon,
  WalletIcon,
} from "lucide-react";
import { useMemo } from "react";
import {
  useExpenseRecordsQuery,
  useExpensesOverviewChartsQuery,
  useExpensesSummaryQuery,
} from "../api/queries";
import { expenseRecordsSearchWithStatus } from "../lib/expense-navigation";
import {
  ExpenseOverviewCategoryChart,
  ExpenseOverviewMonthlyChart,
  ExpenseOverviewStatusChart,
} from "../ui/ExpenseOverviewCharts";
import { ExpenseOverviewStatusBreakdown } from "../ui/ExpenseOverviewStatusBreakdown";
import { ExpenseRecentRecordsCard } from "../ui/ExpenseRecentRecordsCard";
import {
  ExpenseSummaryCards,
  type ExpenseSummaryStatusFilter,
} from "../ui/ExpenseSummaryCards";

export function ExpensesOverviewPage() {
  const nav = useNavigate();
  const canCreate = useActionPermission(["expenses:create"]);
  const canManageCategories = useActionPermission([
    "expenses:manage-categories",
  ]);
  const recentQuery = useMemo(
    () => ({
      limit: 5,
      offset: 0,
      sort: [{ field: "expenseDate", dir: "desc" as const }],
    }),
    [],
  );

  const { data: summary, isLoading: summaryLoading } = useExpensesSummaryQuery(
    {},
  );
  const { data: charts, isLoading: chartsLoading } =
    useExpensesOverviewChartsQuery();
  const { data: recentData, isLoading: recentLoading } =
    useExpenseRecordsQuery(recentQuery);

  const goToRecords = (status: ExpenseSummaryStatusFilter) => {
    void nav({
      to: "/app/expenses",
      search: expenseRecordsSearchWithStatus(status),
    });
  };

  const goToRecordsByStatus = (status: string) => {
    const allowed: ExpenseSummaryStatusFilter[] = [
      "all",
      "submitted",
      "approved",
      "paid",
    ];
    if (allowed.includes(status as ExpenseSummaryStatusFilter)) {
      goToRecords(status as ExpenseSummaryStatusFilter);
      return;
    }
    void nav({
      to: "/app/expenses",
      search: {
        limit: 20,
        offset: 0,
        filters: [{ field: "status", op: "eq", value: status }],
      },
    });
  };

  return (
    <>
      <Header>
        <Breadcrumb items={[{ label: "ແຜງຄວບຄຸມ" }]} />
      </Header>

      <Main>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h1 className="font-semibold text-2xl tracking-tight">
                ແຜງຄວບຄຸມຄ່າໃຊ້ຈ່າຍ
              </h1>
              <p className="mt-1 text-muted-foreground text-sm">
                ພາບລວມຍອດ ແລະ ສະຖານະລາຍການຄ່າໃຊ້ຈ່າຍ
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => nav({ to: "/app/expenses" })}
              >
                <ListIcon className="mr-2 size-4" />
                ລາຍການທັງໝົດ
              </Button>
              {canCreate ? (
                <Button
                  type="button"
                  onClick={() => nav({ to: "/app/expenses/new" })}
                >
                  <PlusIcon className="mr-2 size-4" />
                  ບັນທຶກໃໝ່
                </Button>
              ) : null}
            </div>
          </div>

          <ExpenseSummaryCards
            summary={summary}
            isLoading={summaryLoading}
            statusFilter="all"
            onStatusFilterChange={goToRecords}
          />

          <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
            <div className="flex flex-col gap-6 xl:col-span-8">
              <ExpenseOverviewMonthlyChart
                charts={charts}
                isLoading={chartsLoading}
              />

              <ExpenseOverviewStatusChart
                charts={charts}
                isLoading={chartsLoading}
              />

              <ExpenseOverviewCategoryChart
                charts={charts}
                isLoading={chartsLoading}
              />

              <ExpenseRecentRecordsCard
                records={recentData?.data ?? []}
                isLoading={recentLoading}
              />
            </div>

            <aside className="flex flex-col gap-6 xl:sticky xl:top-6 xl:col-span-4 xl:self-start">
              <Card className="gap-0 py-0">
                <CardHeader className="border-b px-5 py-4">
                  <CardTitle className="text-base">ທາງລັດ</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col gap-2 px-5 py-4">
                  <Button
                    type="button"
                    variant="outline"
                    className="justify-start"
                    onClick={() => nav({ to: "/app/expenses" })}
                  >
                    <WalletIcon className="mr-2 size-4" />
                    ບັນທຶກຄ່າໃຊ້ຈ່າຍ
                  </Button>
                  {canManageCategories ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="justify-start"
                      onClick={() => nav({ to: "/app/expenses/categories" })}
                    >
                      <TagsIcon className="mr-2 size-4" />
                      ຫມວດຄ່າໃຊ້ຈ່າຍ
                    </Button>
                  ) : null}
                  {canCreate ? (
                    <Button
                      type="button"
                      className="justify-start"
                      onClick={() => nav({ to: "/app/expenses/new" })}
                    >
                      <PlusIcon className="mr-2 size-4" />
                      ບັນທຶກໃໝ່
                    </Button>
                  ) : null}
                </CardContent>
              </Card>

              <ExpenseOverviewStatusBreakdown
                summary={summary}
                isLoading={summaryLoading}
                onStatusClick={goToRecordsByStatus}
                compact
              />

              <Card className="gap-0 border-dashed py-0">
                <CardContent className="px-5 py-4">
                  <p className="font-medium text-sm">ຂໍ້ມູນເພີ່ມເຕີມ</p>
                  <p className="mt-2 text-muted-foreground text-xs leading-relaxed">
                    ພາບລວມນີ້ສະແດງຂໍ້ມູນຈາກບັນທຶກຄ່າໃຊ້ຈ່າຍເທົ່ານັ້ນ — ຍັງບໍ່ລວມ
                    payroll, payout ຫຼື refunds
                  </p>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </Main>
    </>
  );
}
